import type { apiTypes } from '@cmg/common';
import { Banner, Panel, ServerErrors, urlUtil } from '@cmg/common';
import memoize from 'memoize-one';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import type { CrmInvestorFirmsFilters } from '../../../common/api/rolodexApiClient';
import LoadingOverlay from '../../../common/components/indicators/loading-overlay/LoadingOverlay';
import type { UUID } from '../../../types/common';
import { fetchCrmIntegration, selectCrmIntegration } from '../shared/ducks';
import CrmIntegrationDetailHeader from './components/crm-integration-detail-header/CrmIntegrationDetailHeader';
import CrmInvestorFirmList from './components/crm-investor-firm-list/CrmInvestorFirmList';
import {
  downloadCrmInvestorFirms,
  fetchCrmInvestorFirms,
  selectCrmInvestorFirms,
  selectCrmInvestorFirmsError,
  selectCrmInvestorFirmsLoading,
  selectCrmInvestorFirmsPagination,
} from './ducks';
import { getQueryParams } from './RolodexCrmIntegrationDetailRoute.model';

const getListParams = memoize(
  (
    totalPages: number,
    page: string | null,
    perPage: string | null,
    orderField: string | null | undefined,
    orderDirection: apiTypes.SortDirection | null | undefined
  ) => ({
    page: totalPages > 0 && totalPages < Number(page) ? totalPages : Number(page),
    perPage: perPage && !isNaN(Number(perPage)) ? Number(perPage) : undefined,
    orderField,
    orderDirection: orderDirection ?? undefined,
  })
);

/**
 * Displays the details for an Account with CRM Integration,
 * including a summary of the firms in the CRM data and a list
 * of the firms in the CRM data.
 */
export const RolodexCrmIntegrationDetailsRouteComponent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { crmIntegrationId } = useParams<{ crmIntegrationId: UUID }>();

  const dispatch = useDispatch();

  const loading = useSelector(selectCrmInvestorFirmsLoading);
  const error = useSelector(selectCrmInvestorFirmsError);
  const clientFirms = useSelector(selectCrmInvestorFirms);
  const crmIntegration = useSelector(selectCrmIntegration);
  const pagination = useSelector(selectCrmInvestorFirmsPagination);

  const {
    searchText,
    linkedStatus,
    firmType,
    page,
    perPage,
    orderField,
    orderDirection,
    firmStatus,
  } = getQueryParams(location.search);

  const queryListParams = getListParams(
    pagination ? pagination.totalPages : 1,
    page,
    perPage,
    orderField,
    orderDirection
  );

  useEffect(() => {
    if (crmIntegrationId) {
      dispatch(
        fetchCrmIntegration({
          crmIntegrationId,
        })
      );

      dispatch(
        fetchCrmInvestorFirms({
          crmIntegrationId,
          ...queryListParams,
          searchText,
          linkedStatus,
          firmType,
          firmStatus,
        })
      );
    }
  }, [dispatch, crmIntegrationId, queryListParams, searchText, linkedStatus, firmType, firmStatus]);

  const handleChangeFilter = (filters: CrmInvestorFirmsFilters) => {
    navigate({
      pathname: location.pathname,
      search: urlUtil.queryStringify({ ...queryListParams, ...filters }),
    });
  };

  const handleChangePage = (params: apiTypes.ListParams) => {
    navigate({
      pathname: location.pathname,
      search: urlUtil.queryStringify({
        searchText,
        linkedStatus,
        firmType,
        firmStatus,
        ...params,
      }),
    });
  };

  const handleDownload = () => {
    if (crmIntegrationId) {
      dispatch(downloadCrmInvestorFirms(crmIntegrationId));
    }
  };

  if (!crmIntegrationId || (loading && !crmIntegration)) {
    return <LoadingOverlay message="Loading..." />;
  }

  if (error) {
    return (
      <Banner variant="error" showIcon={false}>
        <ServerErrors error={error} />
      </Banner>
    );
  }

  const filters = { searchText, linkedStatus, firmType, firmStatus };

  return (
    <React.Fragment>
      <CrmIntegrationDetailHeader />
      <Panel>
        <CrmInvestorFirmList
          crmIntegrationId={crmIntegrationId}
          loading={loading}
          clients={clientFirms}
          pagination={pagination}
          filters={filters}
          onChangeFilters={handleChangeFilter}
          onChangePage={handleChangePage}
          onDownload={handleDownload}
        />
      </Panel>
    </React.Fragment>
  );
};

export default RolodexCrmIntegrationDetailsRouteComponent;
